import React, { useEffect, useRef, useState } from 'react';
import {
    getFunScoreMoment,
    SCORE_ANIMATION_DURATION_MS
} from '../scoreAnimations';
import { getGameNotification, GameNotificationType } from '../gameUtils';
import { subscribeToScoreChanges } from '../scoreEvents';

export default function ScoreAnimationToast({ enabled, settings }) {
    const [moment, setMoment] = useState(null);
    const [imageFailed, setImageFailed] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);
    const timeoutRef = useRef(null);

    useEffect(() => {
        return subscribeToScoreChanges(({ redScore, blueScore }) => {
            if (!enabled) return;

            const gameNotification = getGameNotification(redScore, blueScore, settings);
            if (gameNotification.type !== GameNotificationType.NONE) {
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                    timeoutRef.current = null;
                }
                setMoment(null);
                return;
            }

            const nextMoment = getFunScoreMoment(redScore, blueScore);
            if (!nextMoment) return;

            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            setMoment({
                ...nextMoment,
                key: `${redScore}-${blueScore}-${Date.now()}`
            });
            setImageFailed(false);
            setImageLoaded(false);
            timeoutRef.current = setTimeout(() => {
                setMoment(null);
                timeoutRef.current = null;
            }, SCORE_ANIMATION_DURATION_MS);
        });
    }, [enabled, settings]);

    useEffect(() => {
        if (enabled) return undefined;

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        setMoment(null);

        return undefined;
    }, [enabled]);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    if (!moment) {
        return null;
    }

    return (
        <div
            key={moment.key}
            className="notification-toast score-animation-toast"
            role="status"
            aria-live="polite"
            style={{ '--score-animation-duration': `${SCORE_ANIMATION_DURATION_MS}ms` }}
        >
            <div className="notification-card score-animation-card">
                {imageFailed ? (
                    <div className="score-animation-media">
                        <div className="score-animation-image-fallback" aria-label={moment.image.alt}>
                            {moment.title}
                        </div>
                    </div>
                ) : (
                    <div className="score-animation-media">
                        {!imageLoaded && (
                            <div className="score-animation-image-fallback" aria-label="Loading notification image">
                                {moment.title}
                            </div>
                        )}
                        <img
                            src={moment.image.url}
                            alt={moment.image.alt}
                            className={`score-animation-image ${imageLoaded ? 'loaded' : 'loading'}`}
                            onLoad={() => setImageLoaded(true)}
                            onError={() => {
                                setImageFailed(true);
                                setImageLoaded(false);
                            }}
                        />
                    </div>
                )}
                <div className="notification-copy score-animation-copy">
                    <strong>{moment.title}</strong>
                    <span>{moment.message}</span>
                </div>
            </div>
        </div>
    );
}
